import type { PlayKind, VisitorHost } from '@goodtime/shared';

/**
 * The characters who drop by with a game (Phase 3, `road_story`), and the
 * words on their card. They are Goodtime characters and the card says so:
 * never presented as other players.
 */
export interface VisitorInfo {
  /** Their name on the card's plaque. */
  name: string;
  /** How they introduce themselves. */
  short: string;
  /** Their hello, in their own voice. */
  hello: string;
}

export const VISITORS: Record<VisitorHost, VisitorInfo> = {
  lou: { name: 'LUCKY LOU', short: 'Lou', hello: 'Roll up, roll up!' },
  willa: { name: 'WILD WILLA', short: 'Willa', hello: 'Oi oi! Fancy a quick game?' },
  carl: { name: 'CALM CARL', short: 'Carl', hello: 'Evening. I brought you something.' },
};

/** A visitor's info; an unknown host reads as Lou. */
export const visitorInfo = (host: string | undefined): VisitorInfo => VISITORS[(host ?? 'lou') as VisitorHost] ?? VISITORS.lou;

/** A game's name inside a sentence ("I've brought Smash Three"). */
const GAME_NAMES: Partial<Record<PlayKind, string>> = {
  pick: 'Pick a Prize',
  smash: 'Smash Three',
  match: 'Find the Pairs',
  scratch: 'a Scratch Card',
  wheel: 'the Lucky Wheel',
};

export const gameName = (game: string | undefined): string => GAME_NAMES[game as PlayKind] ?? 'a game';

const n = (v: number) => v.toLocaleString('en-GB');

/** What the visitor says on their card. */
export function visitorLine(host: string | undefined, game: string | undefined): string {
  const v = visitorInfo(host);
  return `${v.hello} I'm ${v.short}, one of the Goodtime characters, and I've brought ${gameName(game)}. Have a go, on me!`;
}

/** What it can pay, said once and plainly: the top and the floor. */
export function visitorPrizeLine(coins: number | undefined, floor: number | undefined): string {
  const top = coins ?? 0;
  const least = floor ?? 0;
  if (top > 0 && least > 0) return `Win up to ${n(top)} coins, and at least ${n(least)} whatever happens.`;
  if (top > 0) return `Win up to ${n(top)} coins.`;
  return 'Free to play.';
}

/** The treasure map: how many pieces are found, said by Fen or the house. */
export function mapPieceLine(pieces: number, of: number, storyOn: boolean): string {
  if (pieces >= of) {
    return storyOn ? `That's all ${of} pieces! X marks the spot. Dig up the treasure!` : `All ${of} pieces: X marks the spot. Dig up the treasure!`;
  }
  const count = `That's ${pieces} of ${of}.`;
  if (!storyOn) return `A piece of a treasure map. ${count} Find them all to dig up the treasure.`;
  return pieces === 1 ? `Look! A piece of an old treasure map. ${count}` : `Another piece of the map! ${count}`;
}

/** The map chip's one-tap explanation. */
export function mapExplain(pieces: number, of: number): string {
  return `Treasure map: ${pieces} of ${of} pieces. Find all ${of} and dig up the treasure at the X.`;
}
